import React, { useState, useEffect, useCallback, useRef } from 'react';
import ReactFlow, {
  ReactFlowProvider,
  Background,
  Controls,
  Handle,
  Position,
  useNodesState,
  useEdgesState,
  addEdge,
  useReactFlow,
} from 'reactflow';
import 'reactflow/dist/style.css';
import { getPipeline, savePipeline, getCustomStages, createCustomStage, updateCustomStage, deleteCustomStage } from '../api/pipelineApi';
import { PREBUILT_STAGES, STAGE_ICONS } from '../constants/pipelineStages';

// ── Custom node ──────────────────────────────────────────────────────────────

// Flow runs top → bottom only. Each stage exposes 3 incoming handles on top
// and 3 outgoing handles on the bottom, so one stage can fan out to (and
// receive from) many others without needing to be placed more than once.
// No left/right handles — all movement is vertical. Handles stay invisible
// until the stage is hovered, or while a connection is being dragged
// anywhere on the canvas (see the "connecting-active" class toggled from
// the canvas wrapper).
function StageNode({ data, selected }) {
  return (
    <div className="stage-node" style={{ ...s.node, ...(data.stageKey === 'lead' ? s.nodeLead : {}), ...(selected ? s.nodeSelected : {}) }}>
      <Handle id="in-top-1" className="stage-node-handle" type="target" position={Position.Top} style={{ ...s.handle, left: '25%' }} />
      <Handle id="in-top-2" className="stage-node-handle" type="target" position={Position.Top} style={{ ...s.handle, left: '50%' }} />
      <Handle id="in-top-3" className="stage-node-handle" type="target" position={Position.Top} style={{ ...s.handle, left: '75%' }} />
      <span style={s.nodeIcon}>{data.icon}</span>
      <span style={s.nodeLabel}>{data.label}</span>
      {data.stageKey === 'lead' && <span style={s.nodeFirstTag}>First stage</span>}
      {data.reachableFromAnyStage && <span style={s.nodeAnyStageTag} title="A lead on any stage can move here directly, no connection needed">⇶ any stage</span>}
      <Handle id="out-bottom-1" className="stage-node-handle" type="source" position={Position.Bottom} style={{ ...s.handle, left: '25%' }} />
      <Handle id="out-bottom-2" className="stage-node-handle" type="source" position={Position.Bottom} style={{ ...s.handle, left: '50%' }} />
      <Handle id="out-bottom-3" className="stage-node-handle" type="source" position={Position.Bottom} style={{ ...s.handle, left: '75%' }} />
    </div>
  );
}

const nodeTypes = { stage: StageNode };

// ── Builder ──────────────────────────────────────────────────────────────────

function newId(prefix) {
  return `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}

function stageToNode(stage, position) {
  return {
    id: newId('n'),
    type: 'stage',
    position,
    deletable: stage.key !== 'lead',
    data: { stageKey: stage.key, label: stage.label, icon: stage.icon, isCustom: !!stage.isCustom, reachableFromAnyStage: false },
  };
}

function BuilderInner({ onBack }) {
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [pipelineName, setPipelineName] = useState('Sales Pipeline');
  const [showUndefinedCard, setShowUndefinedCard] = useState(true);
  const [undefinedCardPosition, setUndefinedCardPosition] = useState('first');
  const [customStages, setCustomStages] = useState([]);
  const [selectedDefKey, setSelectedDefKey] = useState('contacted');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [flash, setFlash] = useState('');
  const [stageModal, setStageModal] = useState(null); // null | { mode: 'create' } | { mode: 'edit', stage }
  const [isConnecting, setIsConnecting] = useState(false); // true while a connection is being dragged — reveals all handles
  const { screenToFlowPosition } = useReactFlow();
  const clickDropCount = useRef(0);
  const dragRowIndex = useRef(null);

  const showFlash = (m) => { setFlash(m); setTimeout(() => setFlash(''), 2500); };

  const fetchCustomStages = useCallback(async () => {
    try { const r = await getCustomStages(); setCustomStages(r.data); } catch (e) {}
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const [pipeRes] = await Promise.all([getPipeline(), fetchCustomStages()]);
        const pipeline = pipeRes.data.pipeline;
        if (pipeline && pipeline.nodes?.length > 0) {
          setPipelineName(pipeline.name || 'Sales Pipeline');
          setShowUndefinedCard(pipeline.showUndefinedStageCard !== false);
          setUndefinedCardPosition(pipeline.undefinedStageCardPosition === 'last' ? 'last' : 'first');
          setNodes(pipeline.nodes.map(n => ({
            id: n.nodeId,
            type: 'stage',
            position: n.position || { x: 100, y: 100 },
            deletable: n.stageKey !== 'lead',
            data: { stageKey: n.stageKey, label: n.label, icon: n.icon, isCustom: n.isCustom, reachableFromAnyStage: !!n.reachableFromAnyStage },
          })));
          setEdges(pipeline.edges.map(e => ({
            id: e.edgeId,
            source: e.sourceNodeId,
            target: e.targetNodeId,
            sourceHandle: e.sourceHandle || null,
            targetHandle: e.targetHandle || null,
          })));
        } else {
          // Fresh pipeline — always starts with the Lead stage at the top
          // (flow runs top → bottom).
          const lead = PREBUILT_STAGES.find(st => st.key === 'lead');
          setNodes([stageToNode(lead, { x: 320, y: 60 })]);
        }
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    })();
  }, [fetchCustomStages, setNodes, setEdges]);

  // All available stage definitions (prebuilt + custom) for the Add dropdown.
  const allDefs = [
    ...PREBUILT_STAGES,
    ...customStages.map(cs => ({ key: `custom_${cs._id}`, label: cs.name, icon: cs.icon, isCustom: true, _id: cs._id })),
  ];
  const selectedDef = allDefs.find(d => d.key === selectedDefKey) || allDefs[0];

  // ── Adding stages ──────────────────────────────────────────────────────────

  const addStage = useCallback((stage) => {
    // Every stage may appear only once — multiple paths are drawn with the
    // multiple connection points on each stage, not duplicate instances.
    if (nodes.some(n => n.data.stageKey === stage.key)) {
      showFlash(`"${stage.label}" is already on the canvas — each stage can only appear once.`);
      return;
    }
    // Stagger placements downward so nodes don't stack exactly (top→bottom flow).
    const offset = (clickDropCount.current++ % 6) * 50;
    const position = screenToFlowPosition({
      x: window.innerWidth / 2 - 60 + (offset % 100),
      y: window.innerHeight / 2 - 100 + offset,
    });
    setNodes(nds => [...nds, stageToNode(stage, position)]);
  }, [nodes, setNodes, screenToFlowPosition]);

  const removeNode = (nodeId) => {
    const node = nodes.find(n => n.id === nodeId);
    if (!node || node.data.stageKey === 'lead') return;
    setNodes(nds => nds.filter(n => n.id !== nodeId));
    setEdges(eds => eds.filter(e => e.source !== nodeId && e.target !== nodeId));
  };

  const toggleReachableFromAnyStage = (nodeId) => {
    setNodes(nds => nds.map(n =>
      n.id === nodeId ? { ...n, data: { ...n.data, reachableFromAnyStage: !n.data.reachableFromAnyStage } } : n
    ));
  };

  // ── Reordering (list order = board column order) ──────────────────────────

  const onRowDragStart = (index) => (event) => {
    dragRowIndex.current = index;
    event.dataTransfer.effectAllowed = 'move';
  };

  const onRowDragOver = (event) => event.preventDefault();

  const onRowDrop = (index) => (event) => {
    event.preventDefault();
    const from = dragRowIndex.current;
    dragRowIndex.current = null;
    if (from === null || from === index) return;
    setNodes(nds => {
      const next = [...nds];
      const [moved] = next.splice(from, 1);
      next.splice(index, 0, moved);
      return next;
    });
  };

  // ── Connections ────────────────────────────────────────────────────────────

  const onConnect = useCallback((connection) => {
    if (connection.source === connection.target) {
      showFlash('A stage cannot connect to itself.');
      return;
    }
    setEdges(eds => {
      // Each stage is placed at most once now, so "already connected" is just
      // a direct source/target node check — no stage-key lookup needed.
      const alreadyConnected = eds.some(e => e.source === connection.source && e.target === connection.target);
      if (alreadyConnected) {
        const targetNode = nodes.find(n => n.id === connection.target);
        showFlash(`This stage is already connected to "${targetNode?.data.label}".`);
        return eds;
      }
      return addEdge({ ...connection, id: newId('e') }, eds);
    });
  }, [nodes, setEdges]);

  // ── Save ───────────────────────────────────────────────────────────────────

  const handleSave = async () => {
    setSaving(true);
    try {
      await savePipeline({
        name: pipelineName,
        showUndefinedStageCard: showUndefinedCard,
        undefinedStageCardPosition: undefinedCardPosition,
        nodes: nodes.map(n => ({
          nodeId: n.id,
          stageKey: n.data.stageKey,
          label: n.data.label,
          icon: n.data.icon,
          isCustom: !!n.data.isCustom,
          position: n.position,
          reachableFromAnyStage: !!n.data.reachableFromAnyStage,
        })),
        edges: edges.map(e => ({
          edgeId: e.id,
          sourceNodeId: e.source,
          targetNodeId: e.target,
          sourceHandle: e.sourceHandle || undefined,
          targetHandle: e.targetHandle || undefined,
        })),
      });
      showFlash('Pipeline saved');
    } catch (e) {
      alert(e.response?.data?.message || 'Failed to save pipeline.');
    } finally { setSaving(false); }
  };

  // ── Custom stages ──────────────────────────────────────────────────────────

  const handleStageModalSave = async ({ name, icon }) => {
    try {
      if (stageModal.mode === 'edit') {
        const r = await updateCustomStage(stageModal.stage._id, { name, icon });
        setNodes(nds => nds.map(n =>
          n.data.stageKey === `custom_${r.data._id}`
            ? { ...n, data: { ...n.data, label: r.data.name, icon: r.data.icon } }
            : n
        ));
        showFlash('Stage updated');
      } else {
        const r = await createCustomStage({ name, icon });
        setSelectedDefKey(`custom_${r.data._id}`);
        showFlash('Stage created');
      }
      setStageModal(null);
      fetchCustomStages();
    } catch (e) {
      alert(e.response?.data?.message || 'Failed to save stage.');
    }
  };

  const handleDeleteCustomStage = async (stage) => {
    if (nodes.some(n => n.data.stageKey === `custom_${stage._id}`)) {
      alert('This stage is placed on the canvas — remove it from the canvas first.');
      return;
    }
    if (!window.confirm(`Delete custom stage "${stage.name}"?`)) return;
    try {
      await deleteCustomStage(stage._id);
      setSelectedDefKey('contacted');
      fetchCustomStages();
      showFlash('Stage deleted');
    } catch (e) {
      alert(e.response?.data?.message || 'Failed to delete stage.');
    }
  };

  if (loading) {
    return <div style={{ ...s.page, alignItems: 'center', justifyContent: 'center', color: '#888' }}>Loading builder...</div>;
  }

  // Disambiguate duplicate stage instances in the ordered list ("· 2", "· 3").
  const seenCounts = {};
  const listRows = nodes.map((n, index) => {
    seenCounts[n.data.stageKey] = (seenCounts[n.data.stageKey] || 0) + 1;
    const dupIndex = seenCounts[n.data.stageKey];
    return { node: n, index, dupIndex };
  });

  return (
    <div style={s.page}>
      {/* Top bar */}
      <div style={s.topBar}>
        <button style={s.backBtn} onClick={onBack}>← Back</button>
        <h1 style={s.heading}>Pipeline Builder</h1>
        <input
          style={s.nameInput}
          value={pipelineName}
          onChange={e => setPipelineName(e.target.value)}
          placeholder="Pipeline name"
        />
        <div style={s.undefinedSettings} title="Leads whose stage was removed from the pipeline show up in a separate card instead of being silently reassigned">
          <label style={s.undefinedSettingsLabel}>
            <input type="checkbox" checked={showUndefinedCard} onChange={e => setShowUndefinedCard(e.target.checked)} />
            Show "Undefined Stages" card
          </label>
          <select
            style={s.undefinedSettingsSelect}
            value={undefinedCardPosition}
            onChange={e => setUndefinedCardPosition(e.target.value)}
            disabled={!showUndefinedCard}
          >
            <option value="first">First position</option>
            <option value="last">Last position</option>
          </select>
        </div>
        <div style={{ flex: 1 }} />
        {flash && <span style={s.flash}>{flash}</span>}
        <button style={s.saveBtn} onClick={handleSave} disabled={saving}>
          {saving ? 'Saving...' : 'Save pipeline'}
        </button>
      </div>

      <div style={s.body}>
        {/* Left bar */}
        <div style={s.leftBar}>
          <div style={s.leftBarSectionTitle}>Add stage</div>
          <div style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
            <select style={s.defSelect} value={selectedDef?.key || ''} onChange={e => setSelectedDefKey(e.target.value)}>
              <optgroup label="Prebuilt">
                {PREBUILT_STAGES.map(d => <option key={d.key} value={d.key}>{d.icon} {d.label}</option>)}
              </optgroup>
              {customStages.length > 0 && (
                <optgroup label="Custom">
                  {customStages.map(cs => <option key={cs._id} value={`custom_${cs._id}`}>{cs.icon} {cs.name}</option>)}
                </optgroup>
              )}
            </select>
            <button style={s.addBtn} onClick={() => selectedDef && addStage(selectedDef)}>Add</button>
          </div>
          {selectedDef?.isCustom && (
            <div style={s.defActions}>
              <button style={s.defActionBtn} onClick={() => setStageModal({ mode: 'edit', stage: customStages.find(cs => cs._id === selectedDef._id) })}>✎ Edit</button>
              <button style={{ ...s.defActionBtn, color: '#A32D2D' }} onClick={() => handleDeleteCustomStage(customStages.find(cs => cs._id === selectedDef._id))}>× Delete</button>
            </div>
          )}
          <button style={s.createStageBtn} onClick={() => setStageModal({ mode: 'create' })}>
            + Create custom stage
          </button>

          <div style={{ ...s.leftBarSectionTitle, marginTop: 18 }}>Stages in this pipeline</div>
          <div style={s.leftBarHint}>Drag to reorder — this order is used for the board columns</div>
          {listRows.map(({ node, index, dupIndex }) => (
            <div
              key={node.id}
              style={s.stageItemWrap}
              draggable
              onDragStart={onRowDragStart(index)}
              onDragOver={onRowDragOver}
              onDrop={onRowDrop(index)}
            >
              <div style={s.stageItem}>
                <span style={s.stageItemGrip}>⠿</span>
                <span style={s.stageItemIcon}>{node.data.icon}</span>
                <span style={s.stageItemLabel}>
                  {node.data.label}{dupIndex > 1 ? ` · ${dupIndex}` : ''}
                </span>
                {node.data.stageKey !== 'lead' && (
                  <button
                    style={{ ...s.stageItemAction, color: '#A32D2D' }}
                    onClick={() => removeNode(node.id)}
                    title="Remove from pipeline"
                  >×</button>
                )}
              </div>
              <label style={s.anyStageCheckLabel} title="A lead sitting on any other stage can move directly here, no connection needed">
                <input
                  type="checkbox"
                  checked={!!node.data.reachableFromAnyStage}
                  onChange={() => toggleReachableFromAnyStage(node.id)}
                />
                Reachable from any stage
              </label>
            </div>
          ))}

          <div style={s.leftBarFootnote}>
            Tip: connect stages by dragging from a stage's bottom dot to another stage's top dot — each stage has several
            connection points on top and bottom so it can lead to (or receive from) many others. Each stage can only be placed once.
            Select a stage or connection and press Delete to remove it. The Lead stage can't be removed.
          </div>
        </div>

        {/* Canvas */}
        <div style={s.canvasWrap} className={isConnecting ? 'connecting-active' : ''}>
          <style>{`
            .stage-node-handle { opacity: 0; transition: opacity 0.12s ease; }
            .stage-node:hover .stage-node-handle,
            .connecting-active .stage-node-handle { opacity: 1; }
          `}</style>
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            onConnectStart={() => setIsConnecting(true)}
            onConnectEnd={() => setIsConnecting(false)}
            nodeTypes={nodeTypes}
            deleteKeyCode={['Backspace', 'Delete']}
            connectionRadius={40}
            fitView
            defaultEdgeOptions={{ animated: true, style: { stroke: '#534AB7', strokeWidth: 1.5 } }}
            proOptions={{ hideAttribution: true }}
          >
            <Background color="#d9d7cf" gap={18} />
            <Controls />
          </ReactFlow>
        </div>
      </div>

      {stageModal && (
        <StageModal
          initial={stageModal.mode === 'edit' ? stageModal.stage : null}
          onClose={() => setStageModal(null)}
          onSave={handleStageModalSave}
        />
      )}
    </div>
  );
}

// ── Custom stage create/edit modal ───────────────────────────────────────────

function StageModal({ initial, onClose, onSave }) {
  const [name, setName] = useState(initial?.name || '');
  const [icon, setIcon] = useState(initial?.icon || STAGE_ICONS[0]);
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setSaving(true);
    await onSave({ name: name.trim(), icon });
    setSaving(false);
  };

  return (
    <div style={s.overlay} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={s.modal}>
        <div style={s.modalHeader}>
          <h2 style={s.modalTitle}>{initial ? `Edit — ${initial.name}` : 'Create custom stage'}</h2>
          <button style={s.modalClose} onClick={onClose}>×</button>
        </div>
        <form onSubmit={submit} style={s.modalBody}>
          <label style={s.fieldLabel}>Stage name</label>
          <input style={s.input} value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Contract Signed" autoFocus />

          <label style={{ ...s.fieldLabel, marginTop: 14 }}>Icon</label>
          <div style={s.iconGrid}>
            {STAGE_ICONS.map(ic => (
              <button
                key={ic}
                type="button"
                style={{ ...s.iconBtn, ...(icon === ic ? s.iconBtnActive : {}) }}
                onClick={() => setIcon(ic)}
              >{ic}</button>
            ))}
          </div>

          <div style={s.modalActions}>
            <button type="button" style={s.cancelBtn} onClick={onClose}>Cancel</button>
            <button type="submit" style={s.submitBtn} disabled={saving || !name.trim()}>
              {saving ? 'Saving...' : initial ? 'Save changes' : 'Create stage'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function PipelineBuilderPage({ onBack }) {
  return (
    <ReactFlowProvider>
      <BuilderInner onBack={onBack} />
    </ReactFlowProvider>
  );
}

// ── Styles ───────────────────────────────────────────────────────────────────

const s = {
  page: { height: '100vh', display: 'flex', flexDirection: 'column', background: '#f5f4f0', fontFamily: 'Inter, system-ui, sans-serif' },
  topBar: { display: 'flex', alignItems: 'center', gap: 14, padding: '0 16px', height: 52, background: '#fff', borderBottom: '1px solid #e0e0e0', flexShrink: 0 },
  backBtn: { fontSize: 13, padding: '6px 12px', borderRadius: 8, border: '1px solid #e0e0e0', background: '#fff', color: '#555', cursor: 'pointer', fontWeight: 500 },
  heading: { fontSize: 16, fontWeight: 600, color: '#1a1a1a', margin: 0 },
  nameInput: { fontSize: 13, padding: '6px 10px', borderRadius: 8, border: '1px solid #e0e0e0', outline: 'none', color: '#1a1a1a', minWidth: 200 },
  undefinedSettings: { display: 'flex', alignItems: 'center', gap: 8, paddingLeft: 12, borderLeft: '1px solid #f0efe9' },
  undefinedSettingsLabel: { display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, color: '#555', cursor: 'pointer', whiteSpace: 'nowrap' },
  undefinedSettingsSelect: { fontSize: 12, padding: '5px 7px', borderRadius: 7, border: '1px solid #e0e0e0', outline: 'none', background: '#fff', color: '#333' },
  flash: { fontSize: 12, color: '#0F6E56', background: '#E1F5EE', padding: '5px 12px', borderRadius: 8, fontWeight: 500 },
  saveBtn: { fontSize: 13, padding: '8px 18px', borderRadius: 8, border: 'none', background: '#534AB7', color: '#fff', cursor: 'pointer', fontWeight: 500 },
  body: { display: 'flex', flex: 1, minHeight: 0 },
  leftBar: { width: 250, background: '#fff', borderRight: '1px solid #e0e0e0', padding: '14px 12px', overflowY: 'auto', flexShrink: 0 },
  leftBarSectionTitle: { fontSize: 11, fontWeight: 600, color: '#888', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 },
  leftBarHint: { fontSize: 11, color: '#aaa', marginBottom: 10, lineHeight: 1.4 },
  defSelect: { flex: 1, fontSize: 13, padding: '7px 8px', borderRadius: 8, border: '1px solid #e0e0e0', outline: 'none', background: '#fff', minWidth: 0 },
  addBtn: { fontSize: 13, padding: '7px 14px', borderRadius: 8, border: 'none', background: '#534AB7', color: '#fff', fontWeight: 500, cursor: 'pointer', flexShrink: 0 },
  defActions: { display: 'flex', gap: 6, marginBottom: 6 },
  defActionBtn: { fontSize: 11, padding: '4px 10px', borderRadius: 6, border: '1px solid #e0e0e0', background: '#fff', color: '#555', cursor: 'pointer' },
  createStageBtn: { width: '100%', marginTop: 4, padding: '8px', borderRadius: 8, border: '1px dashed #534AB7', background: '#fff', color: '#534AB7', fontSize: 12, fontWeight: 500, cursor: 'pointer' },
  stageItemWrap: { borderRadius: 8, border: '1px solid #f0efe9', background: '#fafafa', marginBottom: 6, cursor: 'grab', padding: '2px' },
  stageItem: { display: 'flex', alignItems: 'center', gap: 7, padding: '6px 8px', fontSize: 13 },
  stageItemGrip: { color: '#bbb', fontSize: 12, cursor: 'grab' },
  stageItemIcon: { fontSize: 15 },
  stageItemLabel: { flex: 1, color: '#333', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  stageItemAction: { border: 'none', background: 'none', cursor: 'pointer', color: '#888', fontSize: 14, padding: '0 3px', lineHeight: 1 },
  anyStageCheckLabel: { display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, color: '#777', padding: '0 8px 6px 30px', cursor: 'pointer' },
  leftBarFootnote: { fontSize: 11, color: '#aaa', marginTop: 16, lineHeight: 1.5 },
  canvasWrap: { flex: 1, minWidth: 0 },
  node: { display: 'flex', alignItems: 'center', gap: 7, background: '#fff', border: '1.5px solid #e0e0e0', borderRadius: 10, padding: '10px 14px', fontSize: 13, fontWeight: 600, color: '#1a1a1a', boxShadow: '0 1px 3px rgba(0,0,0,0.07)', position: 'relative', minWidth: 140, justifyContent: 'center' },
  nodeLead: { borderColor: '#534AB7', background: '#EEEDFE' },
  nodeSelected: { borderColor: '#534AB7', boxShadow: '0 0 0 3px rgba(83,74,183,0.18)' },
  nodeIcon: { fontSize: 15 },
  nodeLabel: { whiteSpace: 'nowrap' },
  nodeFirstTag: { fontSize: 9, fontWeight: 600, color: '#534AB7', background: '#fff', borderRadius: 6, padding: '1px 6px', marginLeft: 2 },
  nodeAnyStageTag: { fontSize: 9, fontWeight: 600, color: '#BA7517', background: '#FAEEDA', borderRadius: 6, padding: '1px 6px', marginLeft: 2, whiteSpace: 'nowrap' },
  handle: { width: 14, height: 14, background: '#534AB7', border: '2.5px solid #fff', borderRadius: '50%' },
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1200, padding: 16 },
  modal: { background: '#fff', borderRadius: 16, width: '100%', maxWidth: 420, boxShadow: '0 20px 60px rgba(0,0,0,0.2)', overflow: 'hidden' },
  modalHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 18px', borderBottom: '1px solid #f0efe9' },
  modalTitle: { fontSize: 15, fontWeight: 600, margin: 0, color: '#1a1a1a' },
  modalClose: { background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: '#888', lineHeight: 1 },
  modalBody: { padding: '16px 18px' },
  fieldLabel: { fontSize: 11, fontWeight: 600, color: '#888', textTransform: 'uppercase', letterSpacing: '0.04em', display: 'block', marginBottom: 6 },
  input: { width: '100%', padding: '9px 11px', borderRadius: 8, border: '1px solid #e0e0e0', fontSize: 13, boxSizing: 'border-box', outline: 'none', color: '#1a1a1a', background: '#fafafa' },
  iconGrid: { display: 'grid', gridTemplateColumns: 'repeat(10, 1fr)', gap: 4 },
  iconBtn: { fontSize: 16, padding: '5px 0', borderRadius: 7, border: '1px solid transparent', background: '#fafafa', cursor: 'pointer' },
  iconBtnActive: { borderColor: '#534AB7', background: '#EEEDFE' },
  modalActions: { display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 18 },
  cancelBtn: { padding: '8px 16px', borderRadius: 8, border: '1px solid #e0e0e0', background: '#fff', fontSize: 13, cursor: 'pointer', color: '#555' },
  submitBtn: { padding: '8px 18px', borderRadius: 8, border: 'none', background: '#534AB7', color: '#fff', fontSize: 13, fontWeight: 500, cursor: 'pointer' },
};
